import { createFileRoute, Link } from "@tanstack/react-router";

import { useTransaction } from "../../../contexts/TransactionContext";
import PageTransition from "../../../components/PageTransition";
import { PaymentMethod, WithdrawalType, routePaths } from "../../../constants";
import BackButton from "../../../components/BackButton";
import { requireRouteAccess } from "../../../utils/authorization";

export const Route = createFileRoute("/_app/send/review")({
  beforeLoad: () => requireRouteAccess("/send/review"),
  component: ReviewPage,
});

function ReviewPage() {
  const { state: formData } = useTransaction();
  const {
    withdrawalType,
    fee,
    baseAmount,
    quoteAmount,
    baseCurrency,
    quoteCurrency,
    accountName,
    accountNumber,
    bankName,
    remarks,
    paymentMethod,
  } = formData;

  const isInternational = withdrawalType === WithdrawalType.INTERNATIONAL;

  const amount = isInternational ? quoteAmount : baseAmount;
  const currency = isInternational ? quoteCurrency : baseCurrency;

  // For international: Show gross amount (quoteAmount + fee), then recipient gets quoteAmount
  // For local: fee is added on top of the withdrawal amount
  const grossAmount = isInternational
    ? Number(quoteAmount) + Number(fee)
    : Number(amount);
  const totalAmount = isInternational
    ? Number(quoteAmount)
    : Number(amount) + Number(fee);

  return (
    <PageTransition>
      <div className="withdrawal-review-page bg-light min-h-screen text-gray-800 p-4">
        <div className="flex items-center mb-6">
          <BackButton />
          <h1 className="text-xl font-bold ml-10">Review Transaction</h1>
        </div>

        {/* Transaction Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold mb-4">Transaction Details</h2>

          <div className="space-y-4">
            {isInternational && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">You&apos;re sending</span>
                  <span className="font-medium">
                    {baseAmount} {baseCurrency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-medium">
                    {grossAmount} {quoteCurrency}
                  </span>
                </div>
              </>
            )}

            {!isInternational && (
              <div className="flex justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-medium">
                  {amount} {currency}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Withdrawal Fee</span>
              <span className="font-medium">
                {fee} {isInternational ? quoteCurrency : baseCurrency}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">
                {isInternational ? "Recipient Gets" : "Total Amount"}
              </span>
              <span className="font-medium">
                {totalAmount} {currency}
              </span>
            </div>

            {remarks && (
              <div className="flex justify-between">
                <span className="text-gray-600">Description</span>
                <span className="font-medium">{remarks}</span>
              </div>
            )}
          </div>
        </div>

        {/* Beneficiary Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold mb-4">Beneficiary Details</h2>

          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Account Name</span>
              <span className="font-medium text-right">{accountName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Number</span>
              <span className="font-medium">{accountNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">
                {paymentMethod === PaymentMethod.BANK ? "Bank" : "Provider"}
              </span>
              <span className="font-medium">{bankName}</span>
            </div>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-yellow-600 mt-0.5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Important Notice
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Please review all details carefully. Transactions cannot be
                reversed once confirmed.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 mb-10">
          <Link
            to={routePaths.sendConfirm}
            className="flex justify-center bg-primary text-white py-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Withdrawal
          </Link>

          <Link
            to={routePaths.beneficiary}
            className="block w-full text-center py-4 text-gray-600 font-medium"
          >
            Go Back
          </Link>
        </div>
      </div>
    </PageTransition>
  );
}
